import React from 'react';
import { Plus, Search } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import i18n from '@/utils/i18n';

import {
  categoryLabel,
  memberGlyph,
  type CategoryFilter,
  type ContainerTypeGroup,
  type GroupCategory,
} from './groupHelpers';

const BRAND_GREEN = 'var(--brand-green, oklch(0.685 0.163 145))';

const CATEGORY_CHIPS: { value: CategoryFilter; labelKey: string }[] = [
  { value: 'all', labelKey: 'EditContainerTypeGroups:filterAll' },
  { value: 'Bowls', labelKey: 'EditContainerTypeGroups:categoryBowls' },
  { value: 'Cups', labelKey: 'EditContainerTypeGroups:categoryCups' },
  { value: 'none', labelKey: 'EditContainerTypeGroups:noCategory' },
];

type MobileContainerTypeGroupsProps = {
  /** Groups matching the active search + category filter. */
  rows: ContainerTypeGroup[];
  /** Unfiltered total (the "of M"). */
  totalCount: number;
  search: string;
  onSearchChange: (value: string) => void;
  categoryFilter: CategoryFilter;
  onCategoryFilterChange: (value: CategoryFilter) => void;
  onOpen: (group: ContainerTypeGroup) => void;
  onNew: () => void;
  /** Feedback banner rendered under the header. */
  feedbackSlot?: React.ReactNode;
};

/**
 * Mobile (<768px) presentation of the Container type groups screen: full-width
 * search, wrapping category chips and a single-column card list. Same data and
 * state as desktop — presentation only. The editor sheet is rendered by the
 * parent as a sibling (a bottom sheet).
 */
export default function MobileContainerTypeGroups({
  rows,
  totalCount,
  search,
  onSearchChange,
  categoryFilter,
  onCategoryFilterChange,
  onOpen,
  onNew,
  feedbackSlot,
}: MobileContainerTypeGroupsProps) {
  return (
    <div className="tw:flex tw:flex-col tw:gap-3.5 tw:p-4 tw:pb-16">
      {/* Header */}
      <div className="tw:flex tw:items-end tw:justify-between tw:gap-2.5">
        <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-0.5">
          <h1 className="tw:text-[21px] tw:font-semibold tw:tracking-tight">
            {i18n.t('EditContainerTypeGroups:mobileTitle')}
          </h1>
          <span className="tw:text-[12.5px] tw:text-muted-foreground">
            {i18n.t('EditContainerTypeGroups:countLabel', {
              shown: rows.length,
              total: totalCount,
            })}
          </span>
        </div>
        <Button
          size="icon"
          className="tw:size-11 tw:flex-none"
          aria-label={i18n.t('EditContainerTypeGroups:newGroup')}
          onClick={onNew}
        >
          <Plus size={19} />
        </Button>
      </div>

      {feedbackSlot}

      {/* Search */}
      <div className="tw:relative">
        <Search
          size={17}
          className="tw:pointer-events-none tw:absolute tw:left-3 tw:top-1/2 tw:-translate-y-1/2 tw:text-muted-foreground"
        />
        <input
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={i18n.t('EditContainerTypeGroups:searchPlaceholder')}
          className="tw:h-11 tw:w-full tw:rounded-md tw:border tw:border-input tw:bg-background tw:pl-10 tw:pr-3 tw:text-[15px] tw:text-foreground tw:shadow-xs tw:outline-none"
        />
      </div>

      {/* Category chips */}
      <div className="tw:flex tw:flex-wrap tw:gap-1.5">
        {CATEGORY_CHIPS.map((chip) => {
          const active = categoryFilter === chip.value;
          return (
            <button
              key={chip.value}
              type="button"
              onClick={() => onCategoryFilterChange(chip.value)}
              className={cn(
                'tw:inline-flex tw:h-9 tw:items-center tw:rounded-full tw:px-3.5 tw:text-[13.5px] tw:font-medium tw:transition-colors tw:cursor-pointer',
                active
                  ? 'tw:border tw:border-primary tw:bg-primary tw:text-primary-foreground'
                  : 'tw:border tw:border-border tw:bg-background tw:text-foreground tw:shadow-xs tw:hover:bg-accent',
              )}
            >
              {i18n.t(chip.labelKey)}
            </button>
          );
        })}
      </div>

      {/* Card list */}
      {rows.length === 0 ? (
        <div className="tw:rounded-lg tw:border tw:border-dashed tw:border-border tw:px-4 tw:py-9 tw:text-center tw:text-[13.5px] tw:text-muted-foreground">
          {i18n.t('EditContainerTypeGroups:noGroupsMatch')}
        </div>
      ) : (
        <div className="tw:flex tw:flex-col tw:gap-2.5">
          {rows.map((group) => {
            const Glyph = memberGlyph(
              (group.category as GroupCategory) ?? null,
            );
            const thumbs = group.container_types.slice(0, 4);
            const linked = group.has_linked_stores;
            return (
              <button
                key={group.id}
                type="button"
                onClick={() => onOpen(group)}
                className="tw:flex tw:flex-col tw:gap-2.5 tw:rounded-lg tw:border tw:border-border tw:bg-background tw:p-3.5 tw:text-left tw:shadow-xs tw:transition-colors tw:cursor-pointer tw:active:bg-accent"
              >
                {/* Row 1: name / id + category badge */}
                <div className="tw:flex tw:items-start tw:justify-between tw:gap-2.5">
                  <div className="tw:flex tw:min-w-0 tw:flex-col">
                    <span className="tw:truncate tw:text-[15.5px] tw:font-semibold">
                      {group.name}
                    </span>
                    <span className="tw:font-mono tw:text-[11.5px] tw:text-muted-foreground">
                      #{group.id}
                    </span>
                  </div>
                  {group.category ? (
                    <span className="tw:inline-flex tw:h-[22px] tw:flex-none tw:items-center tw:rounded-md tw:border tw:border-border tw:bg-muted tw:px-2.5 tw:text-xs tw:font-medium">
                      {categoryLabel(group.category as GroupCategory)}
                    </span>
                  ) : null}
                </div>

                {/* Row 2: members + linked */}
                <div className="tw:flex tw:items-center tw:justify-between tw:gap-2.5">
                  <div className="tw:flex tw:min-w-0 tw:items-center tw:gap-2">
                    <div className="tw:flex tw:gap-1.5">
                      {thumbs.map((member) => (
                        <span
                          key={member.id}
                          className="tw:flex tw:size-[30px] tw:items-center tw:justify-center tw:overflow-hidden tw:rounded-lg tw:border tw:border-border tw:bg-muted tw:text-muted-foreground"
                        >
                          {member.image_url ? (
                            <img
                              src={member.image_url}
                              alt={member.name}
                              className="tw:size-full tw:object-cover"
                            />
                          ) : (
                            <Glyph size={15} />
                          )}
                        </span>
                      ))}
                    </div>
                    <span className="tw:whitespace-nowrap tw:text-[12.5px] tw:text-muted-foreground">
                      {group.container_types.length > 0
                        ? i18n.t('EditContainerTypeGroups:typesCount', {
                            count: group.container_types.length,
                          })
                        : i18n.t('EditContainerTypeGroups:noTypesMobile')}
                    </span>
                  </div>
                  <div className="tw:flex tw:flex-none tw:items-center tw:gap-1.5">
                    <span
                      className="tw:size-[7px] tw:flex-none tw:rounded-full"
                      style={{
                        backgroundColor: linked ? BRAND_GREEN : 'var(--border)',
                      }}
                    />
                    <span className="tw:text-[12.5px] tw:text-muted-foreground">
                      {linked
                        ? i18n.t('EditContainerTypeGroups:linked')
                        : i18n.t('EditContainerTypeGroups:notLinked')}
                    </span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
